export function toDoctorsOfficeString(date: Date | undefined) {
  if (!date) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

export function isValidDate(date: Date | undefined): date is Date {
  if (!date) {
    return false;
  }
  return !Number.isNaN(date.getTime());
}

/**
 * Validates a date of birth string in MM/DD/YYYY format.
 * Returns an error message if invalid, or null if valid.
 * @param value - Date string in MM/DD/YYYY format
 * @param minAge - Minimum age required (default: 18)
 * @returns Error message string if invalid, null if valid
 */
export function validateDateOfBirth(value: string, minAge = 18): string | null {
  if (!value || value.trim() === "") {
    return "Date of Birth is required";
  }

  // Parse MM/DD/YYYY format
  const dateMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!dateMatch?.[1] || !dateMatch[2] || !dateMatch[3]) {
    return "Please enter a valid date in MM/DD/YYYY format";
  }

  const month = dateMatch[1];
  const day = dateMatch[2];
  const year = dateMatch[3];
  const date = new Date(
    Number.parseInt(year, 10),
    Number.parseInt(month, 10) - 1,
    Number.parseInt(day, 10),
  );

  // Check if date is valid
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== Number.parseInt(year, 10) ||
    date.getMonth() !== Number.parseInt(month, 10) - 1 ||
    date.getDate() !== Number.parseInt(day, 10)
  ) {
    return "Please enter a valid date";
  }

  // Check if date is in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date >= today) {
    return "Date of birth must be in the past";
  }

  // Check if person meets minimum age requirement
  const age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const dayDiff = today.getDate() - date.getDate();
  const isAtLeastMinAge =
    age > minAge || (age === minAge && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)));

  if (!isAtLeastMinAge) {
    return `You must be at least ${minAge} years old to invest`;
  }

  return null;
}
